import Heading from '../../ui/Heading'
import { useEffect, useState } from 'react'
import api from '../../api'
import { Series } from '../../types'
import Tile, { TileGrid } from '../../ui/Tile'
import PageContent from '../../ui/PageContent'
import { SingleSelect } from '../../ui/form/SingleSelect'
import { Alert, Button, DataTable, JsonPreview, Modal } from '../../ui'
import { useSearchParams } from 'react-router'
import Chart from '../../ui/Chart'
import { redisShard } from '../../utils'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary } from 'react-error-boundary'
import FormWrapper from '../../ui/form/FormWrapper'
import TextInput from '../../ui/form/TextInput'
import toast from 'react-hot-toast/headless'

interface QueueProps {
    metric: Series
    index: number
}
const Queue = ({ metric, index }: QueueProps) => {
    const { t } = useTranslation()
    const [isQueueRunning, setIsQueueRunning] = useState<boolean>(false)
    useEffect(() => {
        api.organizations.getQueueState(metric.label)
            .then((state) => {
                setIsQueueRunning(state)
            })
            .catch(() => {})
    }, [metric])

    const handleToggleQueueState = async () => {
        const isRunning = await api.organizations.setQueueState(metric.label)
        setIsQueueRunning(isRunning)
    }

    const handleClearQueue = async () => {
        if (!window.confirm(t('queue_clear_confirm', { queue: metric.label }))) return
        await api.organizations.clearQueue(metric.label)
        toast.success(t('queue_cleared'))
    }

    return <>
        <Heading
            size="h5" title={`${metric.label} (shard key: ${redisShard(metric.label)})`}
            actions={
                <>
                    <Button
                        size="small"
                        variant="secondary"
                        onClick={async () => await handleToggleQueueState()}
                    >{t(isQueueRunning ? 'queue_pause' : 'queue_start')}</Button>
                    <Button
                        size="small"
                        variant="destructive"
                        onClick={handleClearQueue}
                    >{t('queue_clear')}</Button>
                </>
            }
        />
        <Chart series={[metric]} strokeIndex={index} />
    </>
}

interface JobPerformance {
    throughput: Series[]
    timing: Series[]
}

export default function Performance() {
    const { t } = useTranslation()
    const [waiting, setWaiting] = useState<Record<string, number>>({})

    const [metrics, setMetrics] = useState<Series[] | undefined>()

    const [searchParams, setSearchParams] = useSearchParams()
    const job = searchParams.get('job') ?? undefined

    const [jobs, setJobs] = useState<string[]>([])
    const [currentJob, setCurrentJob] = useState<string | undefined>(job)
    const [jobMetrics, setJobMetrics] = useState<JobPerformance | undefined>()

    const [failed, setFailed] = useState<Array<Record<string, any>>>([])
    const [selectedFailed, setSelectedFailed] = useState<Record<string, any> | undefined>()

    useEffect(() => {
        api.organizations.metrics()
            .then(({ waiting, data, providers = {} }) => {
                const parts = [{
                    label: t('completed'),
                    data: data.map(item => ({
                        date: Date.parse(item.date as string),
                        count: item.count,
                    })),
                }]
                const waitingParts: Record<string, number> = { [t('total_in_queue')]: waiting }
                for (const d of Object.entries(providers)) {
                    const [clientName, clientMetric] = d
                    waitingParts[clientName] = clientMetric.waiting
                    if (clientMetric?.data) {
                        parts.push({
                            label: clientName,
                            data: clientMetric.data.map(item => ({
                                date: new Date(item.date).getTime(),
                                count: item.count,
                            })),
                        })
                    }
                }

                setMetrics(parts)
                setWaiting(waitingParts)
            })
            .catch(() => {})

        api.organizations.jobs()
            .then((jobs) => {
                setJobs(jobs)
                if (!currentJob) setCurrentJob(jobs[0])
            })
            .catch(() => {})

        api.organizations.failed()
            .then((failed) => {
                setFailed(failed)
            })
            .catch(() => {})
    }, [])

    useEffect(() => {
        if (!currentJob) return
        api.organizations.jobPerformance(currentJob)
            .then((series) => {
                setJobMetrics({
                    throughput: series.throughput.map(({ data, label }) => ({ data, label: t(label) })),
                    timing: series.timing.map(({ data, label }) => ({ data, label: t(label) })),
                })
            })
            .catch(() => {})
    }, [currentJob])

    const handleChangeJob = (job: string | undefined) => {
        setCurrentJob(job)
        if (job) {
            searchParams.set('job', job)
            setSearchParams(searchParams)
        }
    }

    const handleClearDeduplicationKey = async ({ key }: any) => {
        await api.organizations.clearDeduplicationKey(key)

        toast.success(t('deduplication_key_cleared'))
    }

    return (
        <PageContent
            title={t('performance')}
            desc={t('performance_desc')}
        >
            <Heading size="h3" title={t('queue')} />
            <Heading size="h4" title={t('throughput')} />
            {metrics && <div>
                <TileGrid numColumns={5}>
                    {Object.entries(waiting).map(([name, count]) => (
                        <Tile key={name} title={count.toLocaleString()} size="large">{name}</Tile>
                    ))}
                </TileGrid>
                <br />
                <Chart series={[metrics[0]]} />

                <Heading size="h4" title={t('individual_queues')} />
                {metrics.map((metric, index) => (
                    (metric.data.length && index > 0)
                        ? <Queue metric={metric} index={index} key={metric.label} />
                        : <></>
                ))}
            </div>}
            <br /><br />
            <Heading size="h3" title={t('individual_job')} actions={
                <SingleSelect
                    size="small"
                    options={jobs}
                    value={currentJob}
                    onChange={handleChangeJob}
                />
            } />
            {jobMetrics && (
                <>
                    <Heading size="h4" title={t('throughput')} />
                    <Chart series={jobMetrics.throughput} />
                    <Heading size="h4" title={t('timing')} />
                    <Chart
                        series={jobMetrics.timing}
                        formatter={(value) => `${value.toLocaleString()}ms`}
                    />
                </>
            )}

            {failed.length && <>
                <Heading size="h4" title={t('failed_jobs')} />
                <ErrorBoundary fallback={<Alert variant="error" title={t('error')}>{t('failed_to_load_failed_jobs')}</Alert>}>
                    <div className="failed">
                        <DataTable items={failed} columns={[
                            { key: 'id', title: t('id') },
                            { key: 'name', title: t('name') },
                            { key: 'attemptsMade', title: t('attempts_made') },
                            { key: 'failedReason', title: t('reason') },
                            {
                                key: 'timestamp',
                                title: t('timestamp'),
                                cell: ({ item: { timestamp } }) => {
                                    const date = new Date(timestamp)
                                    return date.toLocaleString()
                                },
                            },
                        ]} onSelectRow={row => setSelectedFailed(row) }/>
                    </div>
                </ErrorBoundary>
            </>}

            <Heading size="h3" title={t('clear_deduplication_key')} />
            <FormWrapper onSubmit={handleClearDeduplicationKey}>
                {form => (
                    <TextInput.Field form={form} name="key" />
                )}
            </FormWrapper>

            <Modal
                title={t('failed_job')}
                size="large"
                open={!!selectedFailed}
                onClose={() => setSelectedFailed(undefined)}>
                {selectedFailed && <JsonPreview value={selectedFailed} />}
            </Modal>
        </PageContent>
    )
}
